import { motion } from 'framer-motion';
import { Building2, MapPin, Phone, Mail, CheckCircle, XCircle, DollarSign, TrendingUp, Truck, Package } from 'lucide-react';
import Card from '../../components/Card';
import type { BusinessUnit } from '../../services/api';

interface BusinessUnitsCardsProps {
  units: BusinessUnit[];
  isLoading?: boolean;
}

export function BusinessUnitsCards({ units, isLoading }: BusinessUnitsCardsProps) {
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {[1, 2, 3, 4].map((i) => (
          <Card key={i} className="animate-pulse">
            <div className="h-96 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </Card>
        ))}
      </div>
    );
  }

  if (!units || units.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center py-12"
      >
        <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-400" />
        <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          Nenhuma unidade encontrada
        </h3>
        <p style={{ color: 'var(--text-secondary)' }}>
          Não há unidades de negócio cadastradas para esta empresa.
        </p>
      </motion.div>
    );
  }

  const gradients = [
    'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
    'linear-gradient(135deg, #2a5298 0%, #1e3c72 100%)',
    'linear-gradient(135deg, #163965 0%, #1e3c72 100%)',
    'linear-gradient(135deg, #1e3c72 0%, #163965 100%)',
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
    }).format(value);
  };

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  return (
    <div className="space-y-6">
      {/* Header com resumo */}
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-8"
      >
        <h3 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
          🏢 Unidades de Negócio
        </h3>
        <div className="flex justify-center gap-6 flex-wrap">
          <div
            className="text-center px-6 py-4 rounded-xl shadow-lg min-w-[140px]"
            style={{ background: gradients[0], color: 'white' }}
          >
            <div className="text-3xl font-bold">{units.length}</div>
            <div className="text-sm opacity-90">Unidades</div>
          </div>
          <div
            className="text-center px-6 py-4 rounded-xl shadow-lg min-w-[140px]"
            style={{ background: gradients[1], color: 'white' }}
          >
            <div className="text-3xl font-bold">
              {units.filter((u) => u.active).length}
            </div>
            <div className="text-sm opacity-90">Ativas</div>
          </div>
        </div>
      </motion.div>

      {/* Grid de cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {units.map((unit, index) => (
          <motion.div
            key={unit.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <Card className="overflow-hidden min-h-[600px]">
              {/* Header do card com gradiente */}
              <div
                className="px-6 py-5 text-white relative"
                style={{ background: gradients[index % gradients.length] }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="bg-white bg-opacity-20 rounded-full w-14 h-14 flex items-center justify-center text-2xl">
                      🏢
                    </div>
                    <div>
                      <h4 className="text-xl font-bold">{unit.name}</h4>
                      <p className="text-sm opacity-90">Código: {unit.code}</p>
                    </div>
                  </div>
                </div>
                
                {/* Status badge */}
                {unit.active ? (
                  <div className="flex items-center gap-1 bg-green-500 bg-opacity-90 px-3 py-1 rounded-full text-xs font-semibold absolute top-4 right-4">
                    <CheckCircle className="w-3 h-3" />
                    Ativa
                  </div>
                ) : (
                  <div className="flex items-center gap-1 bg-red-500 bg-opacity-90 px-3 py-1 rounded-full text-xs font-semibold absolute top-4 right-4">
                    <XCircle className="w-3 h-3" />
                    Inativa
                  </div>
                )}
              </div>

              {/* Conteúdo do card */}
              <div className="p-6 space-y-4">
                {/* Métricas de Frete - Principais */}
                {unit.freight_data && (
                  <>
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      {/* Menor Custo de Frete */}
                      <div
                        className="text-center p-4 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', border: '1px solid var(--border-primary)' }}
                      >
                        <div className="text-2xl mb-2">💰</div>
                        <div className="text-xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
                          {formatCurrency(unit.freight_data.min_cost)}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Menor Custo de Frete
                        </div>
                      </div>

                      {/* Custo Médio por Km */}
                      <div
                        className="text-center p-4 rounded-xl"
                        style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)', border: '1px solid var(--border-primary)' }}
                      >
                        <div className="text-2xl mb-2">📏</div>
                        <div className="text-xl font-bold mb-1" style={{ color: 'var(--primary)' }}>
                          {formatCurrency(unit.freight_data.avg_cost_per_km)}/km
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Custo Médio
                        </div>
                      </div>
                    </div>

                    {/* Métricas Secundárias */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div
                        className="text-center p-3 rounded-lg"
                        style={{ background: 'rgba(22, 57, 101, 0.05)' }}
                      >
                        <div className="text-lg font-semibold" style={{ color: 'var(--primary)' }}>
                          {formatNumber(unit.freight_data.total_distance)} km
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Distância Total
                        </div>
                      </div>

                      <div
                        className="text-center p-3 rounded-lg"
                        style={{ background: 'rgba(22, 57, 101, 0.05)' }}
                      >
                        <div className="text-lg font-semibold" style={{ color: 'var(--primary)' }}>
                          {formatNumber(unit.freight_data.total_calculations)}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Cálculos
                        </div>
                      </div>
                    </div>

                    {/* Paletes e Entregas */}
                    <div className="grid grid-cols-2 gap-3 mb-4">
                      <div
                        className="text-center p-3 rounded-lg"
                        style={{ background: 'var(--bg-secondary)' }}
                      >
                        <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {formatNumber(unit.freight_data.total_pallets)}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Total Paletes
                        </div>
                      </div>

                      <div
                        className="text-center p-3 rounded-lg"
                        style={{ background: 'var(--bg-secondary)' }}
                      >
                        <div className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
                          {formatNumber(unit.freight_data.total_shipments)}
                        </div>
                        <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                          Total Entregas
                        </div>
                      </div>
                    </div>

                    {/* Veículos Utilizados */}
                    {unit.freight_data.vehicles && unit.freight_data.vehicles.length > 0 && (() => {
                      // Encontrar o veículo com menor custo
                      const cheapestVehicle = unit.freight_data.vehicles.reduce((min, vehicle) => 
                        vehicle.total_cost < min.total_cost ? vehicle : min
                      );
                      
                      // Calcular custo médio por uso
                      const cheapestAvgCost = cheapestVehicle.total_cost / cheapestVehicle.quantity;
                      
                      // Outros veículos (excluindo o mais barato)
                      const otherVehicles = unit.freight_data.vehicles
                        .filter(v => v !== cheapestVehicle)
                        .slice(0, 4);
                      
                      return (
                        <div
                          className="mt-4 pt-4"
                          style={{ borderTop: '2px solid var(--border-primary)' }}
                        >
                          <h5 className="text-sm font-semibold mb-3 flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
                            <Truck className="w-4 h-4" />
                            Veículos Utilizados
                          </h5>
                          
                          {/* Veículo com Menor Custo - DESTAQUE */}
                          <div
                            className="mb-3 p-4 rounded-xl relative overflow-hidden min-h-[120px]"
                            style={{
                              background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)',
                              boxShadow: '0 4px 12px rgba(40, 167, 69, 0.2)'
                            }}
                          >
                            {/* Badge "Melhor Custo" */}
                            <div
                              className="absolute top-3 left-3 px-3 py-1 rounded-lg text-xs font-bold"
                              style={{ background: 'rgba(255, 255, 255, 0.25)', color: 'white' }}
                            >
                              ⭐ MELHOR CUSTO
                            </div>
                            
                            <div className="flex items-start justify-between text-white pt-8">
                              <div className="flex items-center gap-3">
                                <div
                                  className="px-4 py-2 rounded-lg text-2xl font-bold flex items-center justify-center min-w-[60px]"
                                  style={{ background: 'rgba(255, 255, 255, 0.2)' }}
                                >
                                  {cheapestVehicle.quantity}x
                                </div>
                                <div>
                                  <div className="text-xl font-bold mb-1">
                                    {cheapestVehicle.name}
                                  </div>
                                  <div className="text-sm opacity-90">
                                    Média: {formatCurrency(cheapestAvgCost)}/uso
                                  </div>
                                </div>
                              </div>
                            </div>
                            
                            {/* Total no canto inferior direito */}
                            <div className="absolute bottom-3 right-3 text-right">
                              <div className="text-3xl font-bold text-white">
                                {formatCurrency(cheapestVehicle.total_cost)}
                              </div>
                              <div className="text-xs text-white opacity-75">
                                Total
                              </div>
                            </div>
                          </div>
                          
                          {/* Outros Veículos */}
                          {otherVehicles.length > 0 && (
                            <div className="space-y-2 max-h-80 overflow-y-auto">
                              {otherVehicles.map((vehicle, vIndex) => (
                                <div
                                  key={vIndex}
                                  className="flex items-center justify-between p-3 rounded-lg"
                                  style={{
                                    background: vIndex % 2 === 0 ? 'var(--bg-secondary)' : 'var(--bg-primary)',
                                    border: '1px solid var(--border-primary)'
                                  }}
                                >
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="px-2 py-1 rounded-lg text-xs font-bold"
                                      style={{ background: 'rgba(22, 57, 101, 0.1)', color: 'var(--primary)' }}
                                    >
                                      {vehicle.quantity}x
                                    </div>
                                    <span className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                                      {vehicle.name}
                                    </span>
                                  </div>
                                  <span className="text-sm font-bold" style={{ color: 'var(--text-secondary)' }}>
                                    {formatCurrency(vehicle.total_cost)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          
                          {unit.freight_data.vehicles.length > 5 && (
                            <div
                              className="text-center mt-2 py-2 text-xs rounded"
                              style={{ background: 'var(--bg-secondary)', color: 'var(--text-secondary)' }}
                            >
                              + {unit.freight_data.vehicles.length - 5} veículos adicionais
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </>
                )}

                {/* Endereço */}
                {unit.address && (
                  <div
                    className="flex items-start gap-3 mt-4 pt-4"
                    style={{ borderTop: '2px solid var(--border-primary)' }}
                  >
                    <div
                      className="mt-1 p-2 rounded-lg"
                      style={{ background: 'var(--bg-secondary)' }}
                    >
                      <MapPin className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>
                        Endereço
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {unit.address.street}, {unit.address.number}
                        {unit.address.complement && ` - ${unit.address.complement}`}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        {unit.address.neighborhood} - {unit.address.city}/{unit.address.state}
                      </p>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                        CEP: {unit.address.zipCode}
                      </p>
                    </div>
                  </div>
                )}

                {/* Contato */}
                {unit.contact && (unit.contact.phone || unit.contact.email) && (
                  <div className="space-y-2">
                    {unit.contact.phone && (
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ background: 'var(--bg-secondary)' }}
                        >
                          <Phone className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {unit.contact.phone}
                          </p>
                        </div>
                      </div>
                    )}
                    {unit.contact.email && (
                      <div className="flex items-center gap-3">
                        <div
                          className="p-2 rounded-lg"
                          style={{ background: 'var(--bg-secondary)' }}
                        >
                          <Mail className="w-4 h-4" style={{ color: 'var(--primary)' }} />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                            {unit.contact.email}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Card>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
